import { type SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../lib/api.ts';
import { useCountdown } from '../hooks/useCountdown.ts';
import { type AuthResponse, cleanCode, codeErrorMessage, emailLocale } from '../lib/verification.ts';
import { AuthCard, AuthInput, CodeInput, PrimaryButton } from './authUi.tsx';

interface ForgotPasswordProps {
  initialEmail?: string;
  /** Back to the sign-in form. */
  onBack: () => void;
  /** The password was reset: the server signed the user in. */
  onSuccess: (data: AuthResponse) => void;
}

type Step = 'email' | 'reset';

// The server's resend cooldown (plan §4.3); forgot always answers 200, so
// the client keeps its own.
const RESEND_COOLDOWN_S = 60;

async function postJson(path: string, body: object): Promise<{ resp: Response; data: unknown }> {
  const resp = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data: unknown = await resp.json().catch(() => ({}));
  return { resp, data };
}

/**
 * "Forgot password?" (docs/EMAIL_VERIFICATION_PLAN.md §3): email → code and
 * new password → signed in. Never says whether the account exists.
 */
export function ForgotPassword({ initialEmail = '', onBack, onSuccess }: Readonly<ForgotPasswordProps>) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, startCooldown] = useCountdown();

  const sendCode = async (): Promise<boolean> => {
    setLoading(true);
    setError('');
    try {
      const { resp, data } = await postJson('/auth/password/forgot', {
        email: email.trim(),
        locale: emailLocale(),
      });
      if (resp.status === 400) {
        setError(t('auth.forgot.badEmail'));
        return false;
      }
      if (!resp.ok && resp.status !== 429) {
        setError(codeErrorMessage(t, resp.status, data));
        return false;
      }
      startCooldown(RESEND_COOLDOWN_S);
      return true;
    } catch {
      setError(t('auth.cannotReach'));
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    if (await sendCode()) setStep('reset');
  };

  const handleReset = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (code.length !== 6 || !password) return;
    if (password !== confirm) {
      setError(t('auth.forgot.mismatch'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.forgot.tooShort'));
      return;
    }
    setLoading(true);
    setError('');
    try {
      const { resp, data } = await postJson('/auth/password/reset', {
        email: email.trim(),
        code,
        password,
      });
      if (resp.ok) {
        onSuccess(data as AuthResponse);
      } else {
        setError(codeErrorMessage(t, resp.status, data));
      }
    } catch {
      setError(t('auth.cannotReach'));
    } finally {
      setLoading(false);
    }
  };

  const errorLine = error && <p role="alert" className="text-red-400 text-sm text-center">{error}</p>;
  const backLink = (
    <button
      type="button"
      onClick={onBack}
      className="text-xs text-center text-(--color-accent) hover:underline"
    >
      {t('auth.forgot.back')}
    </button>
  );

  if (step === 'email') {
    return (
      <AuthCard title={t('auth.forgot.title')} subtitle={t('auth.forgot.emailIntro')} onSubmit={handleEmail}>
        <AuthInput
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t('auth.forgot.email')}
          aria-label={t('auth.forgot.email')}
          autoComplete="email"
          autoFocus
        />
        {errorLine}
        <PrimaryButton disabled={loading || !email.trim()} loading={loading}>
          {t('auth.forgot.sendCode')}
        </PrimaryButton>
        {backLink}
      </AuthCard>
    );
  }

  const resendLabel = cooldown > 0
    ? t('auth.verify.resendIn', { seconds: cooldown })
    : t('auth.verify.resend');

  return (
    <AuthCard
      title={t('auth.forgot.title')}
      subtitle={t('auth.forgot.sent', { email: email.trim() })}
      onSubmit={handleReset}
    >
      <CodeInput value={code} onChange={(v) => setCode(cleanCode(v))} autoFocus />
      <AuthInput
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={t('auth.forgot.newPassword')}
        aria-label={t('auth.forgot.newPassword')}
        autoComplete="new-password"
      />
      <AuthInput
        type="password"
        value={confirm}
        onChange={(e) => setConfirm(e.target.value)}
        placeholder={t('auth.forgot.confirmPassword')}
        aria-label={t('auth.forgot.confirmPassword')}
        autoComplete="new-password"
      />
      {errorLine}
      <PrimaryButton disabled={loading || code.length !== 6 || !password} loading={loading}>
        {t('auth.forgot.submit')}
      </PrimaryButton>
      <div className="flex flex-col items-center gap-2 text-xs">
        <button
          type="button"
          onClick={() => { void sendCode(); }}
          disabled={cooldown > 0 || loading}
          className="text-(--color-accent) hover:underline disabled:text-(--color-text-secondary) disabled:no-underline disabled:cursor-not-allowed"
        >
          {resendLabel}
        </button>
        {backLink}
      </div>
    </AuthCard>
  );
}
